// Session-scoped Cache API hygiene.
//
// The service worker runs /api/* network-first and stores every successful GET in
// its `fexo-<version>` bucket, keyed by URL alone. Nothing carried a session
// identity, and nothing cleared it on sign-out — so cached payloads outlived the
// session until the next deploy bumped the version. On a shared device that means an
// offline boot after a logout, or a user/role switch, could serve the previous
// caller's data: /api/me, the RBAC context, cross-company admin reads, and the
// per-user device fingerprints from /api/admin/user-devices/{userId} (issue 205).
//
// This purge runs in the PAGE context rather than by messaging the service worker,
// which is what the compromised-device wipe does. Two reasons, both about
// guarantees: the Cache API is fully available on window and needs no owner-side
// coordination (unlike IndexedDB deletion, which the page's own open handles can
// block — the reason that flow messages the SW at all), so the purge is directly
// AWAITABLE and the ordering against the post-logout navigation is deterministic;
// and it still works when no service worker controls the page — a first uncontrolled
// load, or DevTools "update on reload" — where postMessage to a null controller
// silently does nothing.

// sw-logic is imported DYNAMICALLY, not statically. It is otherwise a
// service-worker-only module, so a static import would pull its chunk into the main
// bundle's graph as a blocking request on every app boot — ~334 B and a round-trip on
// the critical path, to serve a code path that runs only at sign-out.
//
// It is no longer shared with the worker. The worker is built in its own pass
// (vite.sw.config.ts) and inlines its copy, because a chunk shared between the two
// entries put an ESM import in sw.js and broke registration outright. So this import
// is the app's alone, and the chunk is not precached: an offline sign-out cannot load
// it. That is why purgeSessionCaches treats the failure as benign — the caches it
// would have purged are unreachable offline anyway, and the next online boot purges
// them before any request is served.
async function cachePolicy(): Promise<typeof import('../sw-logic')> {
  return import('../sw-logic');
}

// purgeSessionCaches deletes every session-scoped entry from every cache this app
// owns, leaving the app shell and hashed assets untouched so offline boot still works
// after a sign-out.
//
// Entry-selective by design: deleting the whole bucket is the compromise wipe's
// semantics and would break offline boot until the worker re-precached.
//
// Best-effort — it never throws. A cache that cannot be opened or enumerated must not
// be able to block a logout: failing to sign out is worse than a stale cached payload,
// and the session is being revoked server-side regardless.
export async function purgeSessionCaches(): Promise<void> {
  if (typeof caches === 'undefined') return; // non-secure context, or no CacheStorage
  try {
    const { CACHE_PREFIX, isSessionScopedPath } = await cachePolicy();
    const names = await caches.keys();
    // Every fexo-* bucket, not just the current version: page code cannot know the
    // build-stamped name, and the worker's offline fallback (caches.match) searches
    // across all of them — so a stale bucket is a live source of stale data.
    await Promise.allSettled(
      names
        .filter((name) => name.startsWith(CACHE_PREFIX))
        .map((name) => purgeCache(name, isSessionScopedPath)),
    );
  } catch {
    // CacheStorage unavailable, enumeration failed, or the policy chunk could not
    // load — nothing more to do here.
  }
}

async function purgeCache(name: string, isSessionScoped: (pathname: string) => boolean): Promise<void> {
  const cache = await caches.open(name);
  const requests = await cache.keys();
  await Promise.allSettled(
    requests
      // A URL that cannot be parsed is left alone: this purge only ever deletes
      // entries it can positively identify as session data.
      .filter((request) => {
        try {
          return isSessionScoped(new URL(request.url).pathname);
        } catch {
          return false;
        }
      })
      .map((request) => cache.delete(request)),
  );
}
